<template>
  <manual-page
    title="Usuarios y Permisos"
    description="Guia completa para administrar usuarios, roles y permisos del sistema."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'adminsIndex' }"
    action-text="Ver usuarios"
  >
    <!-- Overview -->
    <h2>Vista General del Modulo</h2>
    <p>
      El modulo de usuarios y permisos te permite controlar quien tiene acceso al panel
      de administracion y que acciones puede realizar cada persona. Es fundamental
      para mantener la seguridad y organizacion del sistema.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Desde este modulo puedes:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Crear y administrar cuentas de usuario</li>
        <li>Asignar roles con permisos especificos</li>
        <li>Crear roles personalizados segun las necesidades</li>
        <li>Controlar el acceso a cada seccion del sistema</li>
        <li>Monitorear la actividad de los usuarios</li>
        <li>Desactivar cuentas cuando sea necesario</li>
      </ul>
    </div>

    <!-- User List -->
    <h2>Lista de Usuarios</h2>
    <p>
      La lista principal muestra todos los usuarios administradores registrados
      en el sistema. Puedes buscar y filtrar por nombre, email o rol.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Informacion mostrada</h3>
          <p class="text-gray-600 mb-2">Cada usuario en la lista muestra:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre:</strong> Nombre completo del usuario</li>
            <li><strong>Email:</strong> Correo electronico (es su usuario de acceso)</li>
            <li><strong>Rol:</strong> Rol asignado que define sus permisos</li>
            <li><strong>Estado:</strong> Activo o inactivo</li>
            <li><strong>Ultimo acceso:</strong> Fecha y hora del ultimo login</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa periodicamente los usuarios activos y desactiva
        las cuentas que ya no se utilicen para mantener la seguridad del sistema.
      </p>
    </div>

    <!-- Creating Users -->
    <h2>Crear un Usuario</h2>
    <p>
      Para dar acceso a una nueva persona al panel de administracion, sigue estos pasos:
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al formulario</h3>
          <p class="text-gray-600">
            Desde la lista de usuarios, haz clic en "Crear usuario".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Ingresar datos del usuario</h3>
          <p class="text-gray-600 mb-2">Completa la siguiente informacion:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li><strong>Nombre completo:</strong> Nombre y apellidos</li>
            <li><strong>Email:</strong> Sera su usuario para iniciar sesion</li>
            <li><strong>Contraseña:</strong> Contraseña temporal inicial</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Asignar rol</h3>
          <p class="text-gray-600">
            Selecciona el rol apropiado para este usuario. El rol determina
            a que secciones podra acceder y que acciones podra realizar.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Guardar y notificar</h3>
          <p class="text-gray-600">
            Una vez guardado, el usuario recibira un correo con sus credenciales
            de acceso. Es recomendable que cambie su contraseña en el primer inicio de sesion.
          </p>
        </div>
      </div>
    </div>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Asigna siempre el rol con los minimos permisos
        necesarios para la funcion que desempeñara el usuario.
      </p>
    </div>

    <!-- Editing Users -->
    <h2>Editar un Usuario</h2>
    <p>
      Puedes modificar la informacion y permisos de un usuario existente en cualquier momento.
    </p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder al detalle</h3>
          <p class="text-gray-600">
            Desde la lista, haz clic en el usuario que deseas modificar
            para ver su detalle.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Modificar informacion</h3>
          <p class="text-gray-600 mb-2">Puedes cambiar:</p>
          <ul class="list-disc list-inside text-gray-600 space-y-1">
            <li>Nombre y datos de contacto</li>
            <li>Rol asignado</li>
            <li>Estado (activar/desactivar cuenta)</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Restablecer contraseña</h3>
          <p class="text-gray-600">
            Si el usuario olvido su contraseña, puedes restablecerla desde aqui.
            Se le enviara un correo con la nueva contraseña temporal.
          </p>
        </div>
      </div>
    </div>

    <!-- Roles Section -->
    <h2>Gestion de Roles</h2>
    <p>
      Los roles son conjuntos de permisos que definen que puede hacer cada tipo de usuario.
      Puedes usar los roles predefinidos o crear roles personalizados.
    </p>

    <h3>Roles Predefinidos</h3>
    <p>El sistema incluye roles basicos que cubren las funciones mas comunes:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Super Admin</h3>
          <p class="text-gray-600">
            Acceso total a todas las funciones del sistema. Puede crear y eliminar
            otros usuarios, modificar configuraciones criticas y ver toda la informacion.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Administrador</h3>
          <p class="text-gray-600">
            Gestion general del sistema. Puede administrar clientes, mascotas, planes,
            ordenes y la mayoria de las funciones operativas.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Operador</h3>
          <p class="text-gray-600">
            Enfocado en la operacion diaria. Puede gestionar ordenes, ver clientes
            y mascotas, pero no tiene acceso a configuraciones o reportes avanzados.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Contador</h3>
          <p class="text-gray-600">
            Acceso a contabilidad y reportes financieros. Puede ver ingresos, egresos
            y generar reportes, pero no puede modificar datos operativos.
          </p>
        </div>
      </div>
    </div>

    <!-- Creating Roles -->
    <h3>Crear un Rol Personalizado</h3>
    <p>Si los roles predefinidos no se ajustan a tus necesidades, puedes crear roles a medida:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Acceder a Roles</h3>
          <p class="text-gray-600">
            Ve a la seccion de Roles y haz clic en "Crear rol".
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Definir nombre</h3>
          <p class="text-gray-600">
            Asigna un nombre descriptivo que indique claramente la funcion
            del rol (ej: "Supervisor de Entregas", "Atencion al Cliente").
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Seleccionar permisos</h3>
          <p class="text-gray-600">
            Marca los permisos que tendra este rol. Solo activa los permisos
            estrictamente necesarios para la funcion.
          </p>
        </div>
      </div>
    </div>

    <!-- Permissions -->
    <h2>Sistema de Permisos</h2>
    <p>
      Los permisos controlan el acceso granular a cada funcion del sistema.
      Se organizan por tipo de accion y area del sistema.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Tipos de permisos:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>view_*:</strong> Permite ver informacion (solo lectura)</li>
        <li><strong>edit_*:</strong> Permite crear y modificar registros</li>
        <li><strong>delete_*:</strong> Permite eliminar registros</li>
      </ul>
    </div>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Areas de permisos:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li><strong>Clientes:</strong> Gestion de clientes y sus datos</li>
        <li><strong>Mascotas:</strong> Informacion y dietas de mascotas</li>
        <li><strong>Solicitudes:</strong> Solicitudes de cotizacion</li>
        <li><strong>Planes:</strong> Planes de alimentacion</li>
        <li><strong>Ordenes:</strong> Pedidos y entregas</li>
        <li><strong>Franquicias:</strong> Franquicias y sucursales</li>
        <li><strong>Alimentos:</strong> Categorias y plantillas</li>
        <li><strong>Usuarios:</strong> Usuarios y roles</li>
        <li><strong>Contabilidad:</strong> Ingresos y egresos</li>
        <li><strong>Reportes:</strong> Reportes y estadisticas</li>
        <li><strong>Tienda:</strong> Productos y ordenes de compra</li>
      </ul>
    </div>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Sigue el principio de minimo privilegio: asigna solo
        los permisos necesarios para cada rol. Es mas facil agregar permisos despues
        que lidiar con problemas de seguridad.
      </p>
    </div>

    <!-- Best Practices -->
    <h2>Buenas Practicas de Seguridad</h2>
    <ul>
      <li><strong>Contraseñas seguras:</strong> Exige contraseñas fuertes con mayusculas, numeros y simbolos</li>
      <li><strong>Revision periodica:</strong> Audita los usuarios y permisos regularmente</li>
      <li><strong>Cuentas individuales:</strong> Nunca compartas cuentas entre varias personas</li>
      <li><strong>Desactivar inmediatamente:</strong> Cuando alguien deja de trabajar, desactiva su cuenta de inmediato</li>
      <li><strong>Documentar cambios:</strong> Registra cuando y por que se modifican permisos</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualUsers",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualGettingStarted", label: "Primeros pasos" },
        { route: "manualFranchises", label: "Franquicias" },
        { route: "manualAccounting", label: "Contabilidad" },
        { route: "manualReports", label: "Reportes" },
      ],
    };
  },
};
</script>
